import { LANE_COUNT, TILE } from './constants';
import {
  rangerPhaseIndex,
  spawnEnemy,
  updateEnemy,
  type Bolt,
  type Enemy,
  type EnemyWorld,
} from './enemies';
import { cacheMod, type ModId } from './mods';
import { moveBodyX, moveBodyY, overlapsSolid, EPS, type Body } from './physics';
import { T_SOLID, hotAt, tileAt, type Room } from './room';
import { BASE_SUIT, type SuitStats } from './suit';
import { TUNING as T } from './tuning';

export type { Body } from './physics';
export type { Bolt, Enemy, EnemyMode } from './enemies';

/** What the player wants this tick. Touch and keys are turned into this; the sim never sees them. */
export interface Intents {
  /** -1 (left) to 1 (right). */
  readonly moveX: number;
  readonly jump: boolean;
  /** Held: a tap fires a laser; holding 0.3 s then releasing fires a rocket. */
  readonly fire: boolean;
  /** One dash request (a double flick of the stick, or Shift). */
  readonly dash: boolean;
  /** One lane switch request: +1 = one lane farther back, -1 = one lane nearer. */
  readonly lane: -1 | 0 | 1;
}

/** What the suit carries from one room (or transit) into the next. */
export interface Carry {
  readonly starlight: number;
  readonly armor: number;
  /** Weapon mods found earlier in the run. */
  readonly mods?: readonly ModId[];
}

export const NO_INPUT: Intents = { moveX: 0, jump: false, fire: false, dash: false, lane: 0 };

export type PowerState = 'charged' | 'low' | 'flutter' | 'empty';

export function powerState(starlight: number): PowerState {
  if (starlight >= T.chargedAt) return 'charged';
  if (starlight >= T.lowAt) return 'low';
  if (starlight > 0) return 'flutter';
  return 'empty';
}

export interface Player extends Body {
  lane: number;
  facing: 1 | -1;
  grounded: boolean;
  coyote: number;
  jumpBuffer: number;
  jumpHeldPrev: boolean;
  /** True from a jump until the rise ends, while an early release can still cut it short. */
  canCut: boolean;
  jetting: boolean;
  jetTicksThisAir: number;
  fluttering: boolean;
  flutterTicks: number;
  flutterUsed: boolean;
  starlight: number;
  armor: number;
  invuln: number;
  fireCooldown: number;
  fireHeldPrev: boolean;
  /** Ticks FIRE has been held since it was pressed; a rocket is ready at rocketChargeTicks. */
  fireHeld: number;
  dashTicks: number;
  dashCooldown: number;
  airDashUsed: boolean;
  /** Last place the player stood on solid ground with both feet: where a fall respawns. */
  safeX: number;
  safeY: number;
  safeLane: number;
  /** Inside a dust vent's updraft this tick, and the last tick it was. */
  inVent: boolean;
  ventAt: number;
  /** Tick of the last lane switch and the lane it came from. */
  laneChangedAt: number;
  laneFrom: number;
  /** Weapon mods held this run (at most the suit's slots). */
  mods: ModId[];
  /** Inside radiation above the suit's shielding this tick, and the last tick it was. */
  exposed: boolean;
  exposedAt: number;
  /** Radiation dose: a plate burns away each time it reaches radiationTicks. */
  dose: number;
}

export interface Shot {
  readonly id: number;
  readonly kind: 'laser' | 'rocket';
  lane: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  /** Goes through enemies (the piercing beam), hitting each once. */
  readonly pierce: boolean;
  /** Enemies its volley has hit (shared by a spread laser's three shots). */
  readonly hits: number[];
  /** Steers toward the nearest enemy in its lane (homing rockets). */
  readonly homing: boolean;
}

/** A weapon mod cache: what it holds is decided when it is opened. */
export interface Cache {
  readonly id: number;
  readonly lane: number;
  /** Centre in world pixels. */
  readonly x: number;
  readonly y: number;
  taken: boolean;
}

/** A rare material shard: kept through rewinds, spent in the lab. */
export interface Shard {
  readonly id: number;
  readonly lane: number;
  /** Centre in world pixels. */
  readonly x: number;
  readonly y: number;
  taken: boolean;
}

export interface Cell {
  readonly id: number;
  lane: number;
  /** Centre in world pixels. */
  x: number;
  y: number;
  amount: number;
  taken: boolean;
}

export type SimEvent =
  | { type: 'jump' }
  | { type: 'land' }
  | { type: 'jetStart' }
  | { type: 'jetStop' }
  | { type: 'flutterStart' }
  | { type: 'laneSwitch'; from: number; to: number }
  | { type: 'laneBlocked'; to: number }
  | { type: 'ventEnter' }
  /** Stepped into radiation the suit isn't shielded for. */
  | { type: 'radiation' }
  /** A plate burned away by radiation (a `hurt` follows). */
  | { type: 'radiationBurn' }
  /** A weapon mod fitted to a free slot. */
  | { type: 'modFitted'; mod: ModId }
  /** A cache with no slot for its mod (or no mod left to find): broken down into a material. */
  | { type: 'modSalvaged'; mod: ModId | null }
  | { type: 'fire'; shotId: number }
  | { type: 'rocketReady' }
  | { type: 'rocketFire'; shotId: number }
  | { type: 'blast'; lane: number; x: number; y: number }
  | { type: 'dash' }
  | { type: 'enemyHit'; id: number }
  | { type: 'enemyKilled'; id: number }
  | { type: 'enemyBlocked'; id: number }
  | { type: 'droneAim'; id: number }
  | { type: 'boltFire'; id: number }
  | { type: 'bossTell' }
  | {
      type: 'bossHop';
      id: number;
      from: number;
      to: number;
      /** Centres where it left and where it landed. */
      fromX: number;
      fromY: number;
      toX: number;
      toY: number;
    }
  | { type: 'bossCharge' }
  | { type: 'bossSlam' }
  | { type: 'bossPhase'; phase: number }
  | { type: 'bossDefeated'; id: number }
  | { type: 'pickup'; amount: number }
  | { type: 'shard' }
  | { type: 'hurt' }
  | { type: 'fell' }
  | { type: 'rewind' }
  | { type: 'roomExit' };

export const PLAYER_W = 10;
export const PLAYER_H = 22;
/** The player's hurtbox is smaller than its collision box (forgiveness). */
const HURT_INSET = 2;

/**
 * The whole game state for one room, stepped at a fixed rate. Pure TypeScript: no Phaser, no
 * browser, no clock, so it runs the same in tests and in the game.
 */
export class Simulation {
  tick = 0;
  player!: Player;
  enemies: Enemy[] = [];
  shots: Shot[] = [];
  /** Hostile shots (drones, the Ranger). */
  bolts: Bolt[] = [];
  cells: Cell[] = [];
  shards: Shard[] = [];
  caches: Cache[] = [];
  /** Times the suit has rewound in this session (the roguelite loop, in miniature). */
  rewinds = 0;
  /** Set once the player touches an exit; the room is finished. */
  exited = false;
  private events: SimEvent[] = [];
  private nextId = 1;

  constructor(
    readonly room: Room,
    /** What the suit carries in from the previous room (absent: full, no mods). */
    carry?: Carry,
    /** The suit as the lab has built it. */
    readonly suit: SuitStats = BASE_SUIT,
  ) {
    this.reset();
    if (carry) {
      this.player.starlight = Math.min(carry.starlight, suit.starlightMax);
      this.player.armor = Math.min(carry.armor, suit.armorMax);
      this.player.mods = (carry.mods ?? []).slice(0, suit.modSlots);
    }
  }

  /** Back to the room's starting state, the suit at full power (the constructor applies a carry). */
  reset(): void {
    const { start } = this.room;
    this.player = {
      x: start.tx * TILE + (TILE - PLAYER_W) / 2,
      y: (start.ty + 1) * TILE - PLAYER_H,
      w: PLAYER_W,
      h: PLAYER_H,
      vx: 0,
      vy: 0,
      lane: start.lane,
      facing: 1,
      grounded: false,
      coyote: 0,
      jumpBuffer: 0,
      jumpHeldPrev: false,
      canCut: false,
      jetting: false,
      jetTicksThisAir: 0,
      fluttering: false,
      flutterTicks: 0,
      flutterUsed: false,
      starlight: this.suit.starlightMax,
      armor: this.suit.armorMax,
      invuln: 0,
      fireCooldown: 0,
      fireHeldPrev: false,
      fireHeld: 0,
      dashTicks: 0,
      dashCooldown: 0,
      airDashUsed: false,
      safeX: start.tx * TILE + (TILE - PLAYER_W) / 2,
      safeY: (start.ty + 1) * TILE - PLAYER_H,
      safeLane: start.lane,
      inVent: false,
      ventAt: -1000,
      laneChangedAt: -1000,
      laneFrom: start.lane,
      mods: [],
      exposed: false,
      exposedAt: -1000,
      dose: 0,
    };
    this.enemies = this.room.spawns.map((s) => spawnEnemy(this.nextId++, s));
    this.shots = [];
    this.bolts = [];
    this.cells = this.room.cells.map(({ lane, tx, ty }) => ({
      id: this.nextId++,
      lane,
      x: tx * TILE + TILE / 2,
      y: ty * TILE + TILE / 2,
      amount: T.cellStarlight,
      taken: false,
    }));
    this.shards = this.room.shards.map(({ lane, tx, ty }) => ({
      id: this.nextId++,
      lane,
      x: tx * TILE + TILE / 2,
      y: ty * TILE + TILE / 2,
      taken: false,
    }));
    this.caches = this.room.caches.map(({ lane, tx, ty }) => ({
      id: this.nextId++,
      lane,
      x: tx * TILE + TILE / 2,
      y: ty * TILE + TILE / 2,
      taken: false,
    }));
  }

  /** What the suit carries out of this room. */
  carry(): Carry {
    const p = this.player;
    return { starlight: p.starlight, armor: p.armor, mods: [...p.mods] };
  }

  /** Advances one tick and returns what happened during it. */
  step(input: Intents): readonly SimEvent[] {
    this.events = [];
    this.tick++;
    this.updatePlayer(input);
    this.updateRadiation();
    this.updateEnemies();
    this.updateShots();
    this.updateBolts();
    this.updateContacts();
    this.checkExit();
    return this.events;
  }

  private emit(event: SimEvent): void {
    this.events.push(event);
  }

  private updatePlayer(input: Intents): void {
    const p = this.player;
    if (p.invuln > 0) p.invuln--;
    if (p.fireCooldown > 0) p.fireCooldown--;
    if (p.dashCooldown > 0) p.dashCooldown--;

    // Running.
    const moveX = Math.max(-1, Math.min(1, input.moveX));
    if (moveX !== 0 && p.dashTicks === 0) p.facing = moveX > 0 ? 1 : -1;
    const target = moveX * T.runMax;
    const accel = p.grounded ? T.groundAccel : T.airAccel;
    const friction = p.grounded ? T.groundFriction : T.airFriction;
    if (moveX !== 0) p.vx = approach(p.vx, target, accel);
    else p.vx = approach(p.vx, 0, friction);

    if (input.dash) this.startDash();

    // Jumping, with a jump buffer and coyote time.
    const jumpPressed = input.jump && !p.jumpHeldPrev;
    p.jumpHeldPrev = input.jump;
    if (jumpPressed) p.jumpBuffer = T.jumpBufferTicks;
    else if (p.jumpBuffer > 0) p.jumpBuffer--;
    if (p.jumpBuffer > 0 && p.coyote > 0) {
      p.vy = T.jumpVelocity;
      p.jumpBuffer = 0;
      p.coyote = 0;
      p.grounded = false;
      p.canCut = true;
      this.emit({ type: 'jump' });
    }
    if (p.canCut && !input.jump && p.vy < 0) {
      p.vy *= T.jumpCut;
      p.canCut = false;
    }
    if (p.vy >= 0) p.canCut = false;

    if (p.dashTicks > 0) {
      // A dash is a straight line: no gravity, no thrust, full speed the way the player faces.
      p.dashTicks--;
      p.vx = p.facing * (p.dashTicks > 0 ? T.dashSpeed : T.runMax);
      p.vy = 0;
      if (p.jetting) this.emit({ type: 'jetStop' });
      p.jetting = false;
      p.fluttering = false;
      if (p.dashTicks === 0) p.dashCooldown = T.dashCooldownTicks;
    } else {
      p.vy = Math.min(p.vy + T.gravity * this.room.gravity, T.maxFall * this.room.gravity);
      this.updateThrust(input);
    }

    // Dust vents push the suit up for free (a dash goes straight through them).
    const inVent = p.dashTicks === 0 && this.inVent(p);
    // It only speeds up a slower rise: a jump through a vent keeps its own speed.
    if (inVent && p.vy > T.ventMaxRise) p.vy = Math.max(p.vy - T.ventLift, T.ventMaxRise);
    // Announced once per ride, not each time the suit bobs back in at the top of the column.
    if (inVent && this.tick - p.ventAt > 30) this.emit({ type: 'ventEnter' });
    if (inVent) p.ventAt = this.tick;
    p.inVent = inVent;

    // Move and collide, one axis at a time.
    if (moveBodyX(this.room, p.lane, p, p.vx)) p.vx = 0;
    const wasGrounded = p.grounded;
    const hit = moveBodyY(this.room, p.lane, p, p.vy);
    p.grounded = hit === 'floor';
    if (hit !== 'none') p.vy = 0;
    if (p.grounded) {
      p.coyote = T.coyoteTicks;
      p.jetTicksThisAir = 0;
      p.flutterUsed = false;
      p.airDashUsed = false;
      if (this.onSolidGround(p)) {
        p.safeX = p.x;
        p.safeY = p.y;
        p.safeLane = p.lane;
      }
      // Radiation the suit isn't shielded for stops the reactor refilling: an empty suit stays
      // empty in it, and burns twice as fast.
      if (!this.irradiated(p)) {
        p.starlight = Math.min(
          this.suit.starlightMax,
          p.starlight + this.suit.regenPerTickGrounded,
        );
      }
      if (!wasGrounded) this.emit({ type: 'land' });
    } else if (p.coyote > 0) {
      p.coyote--;
    }

    if (input.lane !== 0) this.switchLane(p.lane + input.lane);
    this.updateFire(input.fire);

    // Fell out of the bottom of the room: lose a plate and go back to the last safe ground.
    if (p.y > this.room.height + TILE * 2) {
      this.emit({ type: 'fell' });
      this.damagePlayer(0);
      if (this.player === p && p.armor > 0) this.respawn();
    }
  }

  /** Jetpack and flutter: both come from holding jump in the air. */
  private updateThrust(input: Intents): void {
    const p = this.player;
    const airborne = !p.grounded || p.vy < 0;
    const holding = airborne && input.jump;
    const engaged = p.jetting || p.fluttering || p.vy >= T.thrustEngageVy;
    let jet = false;
    let flutter = false;
    if (holding && engaged) {
      const state = powerState(p.starlight);
      if (p.fluttering) {
        flutter = p.flutterTicks > 0;
      } else if (state === 'charged' || (state === 'low' && p.jetTicksThisAir < T.lowJetTicks)) {
        jet = true;
      } else if (p.starlight > 0 && !p.flutterUsed) {
        flutter = true;
        p.flutterUsed = true;
        p.flutterTicks = T.flutterTicks;
        this.emit({ type: 'flutterStart' });
      }
    }

    if (jet) {
      if (!p.jetting) this.emit({ type: 'jetStart' });
      p.vy = Math.max(p.vy - T.jetAccel, T.jetMaxRise);
      p.starlight = Math.max(0, p.starlight - this.suit.jetDrainPerTick);
      p.jetTicksThisAir++;
      p.canCut = false;
    } else if (p.jetting) {
      this.emit({ type: 'jetStop' });
    }
    p.jetting = jet;

    if (flutter) {
      p.vy = Math.max(p.vy - T.flutterLift, T.flutterMaxRise);
      p.starlight = Math.max(0, p.starlight - T.flutterDrainPerTick);
      p.flutterTicks--;
      p.canCut = false;
    }
    p.fluttering = flutter;
  }

  private switchLane(to: number): void {
    const p = this.player;
    if (to < 0 || to >= LANE_COUNT) {
      this.emit({ type: 'laneBlocked', to });
      return;
    }
    if (overlapsSolid(this.room, to, p)) {
      this.emit({ type: 'laneBlocked', to });
      return;
    }
    p.laneFrom = p.lane;
    p.lane = to;
    p.laneChangedAt = this.tick;
    this.emit({ type: 'laneSwitch', from: p.laneFrom, to });
  }

  private startDash(): void {
    const p = this.player;
    if (p.dashTicks > 0 || p.dashCooldown > 0 || p.starlight < T.dashCost) return;
    if (!p.grounded) {
      if (p.airDashUsed) return;
      p.airDashUsed = true;
    }
    p.dashTicks = T.dashTicks;
    p.starlight -= T.dashCost;
    this.emit({ type: 'dash' });
  }

  /**
   * FIRE: pressing shoots a laser at once. Keeping it held charges a rocket (Charged power only),
   * which launches on release. A release before the charge completes fires nothing more.
   */
  private updateFire(held: boolean): void {
    const p = this.player;
    const pressed = held && !p.fireHeldPrev;
    const released = !held && p.fireHeldPrev;
    p.fireHeldPrev = held;
    if (pressed) {
      p.fireHeld = 0;
      if (p.fireCooldown === 0) this.shoot('laser');
    } else if (held) {
      p.fireHeld++;
      if (p.fireHeld === T.rocketChargeTicks && powerState(p.starlight) === 'charged') {
        this.emit({ type: 'rocketReady' });
      }
    } else if (released) {
      const charged = p.fireHeld >= T.rocketChargeTicks;
      p.fireHeld = 0;
      if (charged && powerState(p.starlight) === 'charged') {
        p.starlight -= T.rocketCost;
        this.shoot('rocket');
      }
    }
  }

  /** True while FIRE has been held long enough to launch a rocket on release. */
  rocketCharged(): boolean {
    const p = this.player;
    return (
      p.fireHeldPrev && p.fireHeld >= T.rocketChargeTicks && powerState(p.starlight) === 'charged'
    );
  }

  /**
   * A shot from the forearm, auto-aimed at the nearest enemy in the player's lane. Weapon mods
   * change it: the spread laser fires a fan of three, the piercing beam goes through enemies, and
   * homing rockets steer.
   */
  private shoot(kind: Shot['kind']): void {
    const p = this.player;
    const cx = p.x + p.w / 2;
    const cy = p.y + p.h / 2 - 3;
    const target = this.nearestEnemy(cx, cy, p.lane);
    let angle = p.facing > 0 ? 0 : Math.PI;
    if (target) {
      angle = Math.atan2(target.y + target.h / 2 - cy, target.x + target.w / 2 - cx);
      p.facing = Math.cos(angle) >= 0 ? 1 : -1;
    }
    const rocket = kind === 'rocket';
    const speed = rocket ? T.rocketSpeed : T.laserSpeed;
    const fan = !rocket && p.mods.includes('spread') ? [-T.spreadAngle, 0, T.spreadAngle] : [0];
    // One list for the whole volley: a fan hits each enemy once, however many of it connect.
    const hits: number[] = [];
    let first = 0;
    for (const turn of fan) {
      const shot: Shot = {
        id: this.nextId++,
        kind,
        lane: p.lane,
        x: cx + p.facing * 6,
        y: cy,
        vx: Math.cos(angle + turn) * speed,
        vy: Math.sin(angle + turn) * speed,
        life: rocket ? T.rocketLifeTicks : T.laserLifeTicks,
        pierce: !rocket && p.mods.includes('pierce'),
        hits,
        homing: rocket && p.mods.includes('homing'),
      };
      first ||= shot.id;
      this.shots.push(shot);
    }
    p.fireCooldown = T.fireCooldownTicks;
    this.emit(rocket ? { type: 'rocketFire', shotId: first } : { type: 'fire', shotId: first });
  }

  nearestEnemy(x: number, y: number, lane: number): Enemy | undefined {
    let best: Enemy | undefined;
    let bestDist: number = T.aimRange;
    for (const e of this.enemies) {
      if (!e.alive || e.lane !== lane) continue;
      const d = Math.hypot(e.x + e.w / 2 - x, e.y + e.h / 2 - y);
      if (d < bestDist) {
        best = e;
        bestDist = d;
      }
    }
    return best;
  }

  private updateEnemies(): void {
    const world: EnemyWorld = {
      room: this.room,
      player: this.player,
      tick: this.tick,
      emit: (event) => this.emit(event),
      fireBolt: (from, vx, vy) => this.fireBolt(from, vx, vy),
    };
    for (const e of this.enemies) if (e.alive) updateEnemy(e, world);
  }

  private fireBolt(from: Enemy, vx: number, vy: number): void {
    const bolt: Bolt = {
      id: this.nextId++,
      lane: from.lane,
      x: from.x + from.w / 2,
      y: from.y + from.h / 2,
      vx,
      vy,
      life: T.boltLifeTicks,
    };
    this.bolts.push(bolt);
    this.emit({ type: 'boltFire', id: bolt.id });
  }

  private updateBolts(): void {
    for (const b of this.bolts) {
      b.x += b.vx;
      b.y += b.vy;
      b.life--;
      if (tileAt(this.room, b.lane, Math.floor(b.x / TILE), Math.floor(b.y / TILE)) === T_SOLID) {
        b.life = 0;
      }
    }
    this.bolts = this.bolts.filter((b) => b.life > 0);
  }

  private updateShots(): void {
    for (const s of this.shots) {
      if (s.homing) this.steer(s);
      s.x += s.vx;
      s.y += s.vy;
      s.life--;
      const hitWall =
        tileAt(this.room, s.lane, Math.floor(s.x / TILE), Math.floor(s.y / TILE)) === T_SOLID;
      const target = this.enemies.find(
        (e) =>
          e.alive &&
          e.lane === s.lane &&
          !s.hits.includes(e.id) &&
          s.x >= e.x - 2 &&
          s.x <= e.x + e.w + 2 &&
          s.y >= e.y - 2 &&
          s.y <= e.y + e.h + 2,
      );
      if (s.kind === 'rocket') {
        // Rockets burst on anything, or at the end of their flight, hitting all nearby enemies. A
        // homing rocket also bursts on passing close: otherwise it could circle a target inside its
        // turning circle until it ran out.
        if (hitWall || target || s.life <= 0 || (s.homing && this.closeToEnemy(s))) {
          s.life = 0;
          this.blast(s.lane, s.x, s.y);
        }
      } else if (hitWall) {
        s.life = 0;
      } else if (target) {
        s.hits.push(target.id);
        // A piercing beam flies on, unless the Ranger's guard stops it.
        const landed = this.damageEnemy(target, 1);
        if (!s.pierce || !landed) s.life = 0;
      }
    }
    this.shots = this.shots.filter((s) => s.life > 0);
  }

  /** True when an enemy in the shot's lane is well inside a rocket's blast. */
  private closeToEnemy(s: Shot): boolean {
    const fuse = T.blastRadius * 0.75;
    return this.enemies.some(
      (e) =>
        e.alive && e.lane === s.lane && Math.hypot(e.x + e.w / 2 - s.x, e.y + e.h / 2 - s.y) < fuse,
    );
  }

  /** Turns a homing rocket a little toward the nearest enemy in its lane, keeping its speed. */
  private steer(s: Shot): void {
    const target = this.nearestEnemy(s.x, s.y, s.lane);
    if (!target) return;
    const heading = Math.atan2(s.vy, s.vx);
    const want = Math.atan2(target.y + target.h / 2 - s.y, target.x + target.w / 2 - s.x);
    // The shortest way round, at most homingTurn a tick.
    const diff = Math.atan2(Math.sin(want - heading), Math.cos(want - heading));
    const turned = heading + Math.max(-T.homingTurn, Math.min(T.homingTurn, diff));
    const speed = Math.hypot(s.vx, s.vy);
    s.vx = Math.cos(turned) * speed;
    s.vy = Math.sin(turned) * speed;
  }

  private blast(lane: number, x: number, y: number): void {
    this.emit({ type: 'blast', lane, x, y });
    for (const e of this.enemies) {
      if (!e.alive || e.lane !== lane) continue;
      const d = Math.hypot(e.x + e.w / 2 - x, e.y + e.h / 2 - y);
      if (d <= T.blastRadius) this.damageEnemy(e, T.rocketDamage);
    }
  }

  /** Damages an enemy; false when the hit glanced off (the Ranger's guard). */
  private damageEnemy(e: Enemy, amount: number): boolean {
    // The Ranger guards: shots glance off unless it is dazed after a charge.
    if (e.kind === 'ranger' && e.mode !== 'recover') {
      this.emit({ type: 'enemyBlocked', id: e.id });
      return false;
    }
    const phaseBefore = rangerPhaseIndex(e);
    e.hp -= amount;
    e.hitFlash = 6;
    this.emit({ type: 'enemyHit', id: e.id });
    if (e.hp > 0) {
      const phase = rangerPhaseIndex(e);
      if (e.kind === 'ranger' && phase !== phaseBefore) this.emit({ type: 'bossPhase', phase });
      return true;
    }
    e.alive = false;
    this.emit({ type: 'enemyKilled', id: e.id });
    if (e.kind === 'ranger') this.emit({ type: 'bossDefeated', id: e.id });
    // A boss spills full cells; anything else drops a small one.
    const drops = e.kind === 'ranger' ? [-12, 0, 12] : [0];
    for (const dx of drops) {
      this.cells.push({
        id: this.nextId++,
        lane: e.lane,
        x: e.x + e.w / 2 + dx,
        y: e.y + e.h / 2 - 4,
        amount: e.kind === 'ranger' ? T.cellStarlight : T.dropStarlight,
        taken: false,
      });
    }
    return true;
  }

  /** True while a boss in this room is still standing (the exits stay shut). */
  bossAlive(): boolean {
    return this.enemies.some((e) => e.kind === 'ranger' && e.alive);
  }

  private updateContacts(): void {
    const p = this.player;
    // Pickups are collected a little outside the player's box.
    const touching = (it: { taken: boolean; lane: number; x: number; y: number }) =>
      !it.taken &&
      it.lane === p.lane &&
      it.x >= p.x - 4 &&
      it.x <= p.x + p.w + 4 &&
      it.y >= p.y - 4 &&
      it.y <= p.y + p.h + 4;
    for (const c of this.cells) {
      if (touching(c)) {
        c.taken = true;
        p.starlight = Math.min(this.suit.starlightMax, p.starlight + c.amount);
        this.emit({ type: 'pickup', amount: c.amount });
      }
    }
    for (const sh of this.shards) {
      if (touching(sh)) {
        sh.taken = true;
        this.emit({ type: 'shard' });
      }
    }
    for (const c of this.caches) {
      if (touching(c)) {
        c.taken = true;
        this.openCache(c);
      }
    }
    // Invulnerable after a hit, and for the whole of a dash.
    if (p.invuln > 0 || p.dashTicks > 0) return;
    for (const b of this.bolts) {
      if (b.lane !== p.lane) continue;
      const inside =
        b.x > p.x + HURT_INSET &&
        b.x < p.x + p.w - HURT_INSET &&
        b.y > p.y + HURT_INSET &&
        b.y < p.y + p.h;
      if (inside) {
        b.life = 0;
        this.damagePlayer(b.vx >= 0 ? 1 : -1);
        return;
      }
    }
    for (const e of this.enemies) {
      if (!e.alive || e.lane !== p.lane) continue;
      const overlap =
        p.x + HURT_INSET < e.x + e.w &&
        p.x + p.w - HURT_INSET > e.x &&
        p.y + HURT_INSET < e.y + e.h &&
        p.y + p.h > e.y;
      if (overlap) {
        const away = p.x + p.w / 2 >= e.x + e.w / 2 ? 1 : -1;
        this.damagePlayer(away);
        return;
      }
    }
  }

  /** Loses one armor plate; at zero the suit rewinds time to the start of the room. */
  private damagePlayer(away: -1 | 0 | 1): void {
    const p = this.player;
    p.armor--;
    p.invuln = T.invulnTicks;
    if (away !== 0) {
      p.vx = away * T.knockbackVx;
      p.vy = T.knockbackVy;
    }
    this.emit({ type: 'hurt' });
    if (p.armor <= 0) {
      this.rewinds++;
      this.reset();
      this.emit({ type: 'rewind' });
    }
  }

  /** Fits the cache's mod to a free slot; with no slot free (or no mod left), salvages it. */
  private openCache(c: Cache): void {
    const p = this.player;
    const mod = cacheMod(`${this.room.id}/cache:${c.lane},${c.x},${c.y}`, p.mods);
    if (mod && p.mods.length < this.suit.modSlots) {
      p.mods.push(mod);
      this.emit({ type: 'modFitted', mod });
    } else {
      this.emit({ type: 'modSalvaged', mod });
      this.emit({ type: 'shard' });
    }
  }

  /**
   * Radiation above the suit's shielding builds a dose while the player is in it (twice as fast
   * with no Starlight); each full dose burns away a plate. Out of it, the dose wears off.
   */
  private updateRadiation(): void {
    const p = this.player;
    const exposed = this.irradiated(p);
    if (!exposed) {
      p.exposed = false;
      if (p.dose > 0) p.dose--;
      return;
    }
    // Announced once per stay, not each time the suit dips out and back in at a zone's edge.
    if (!p.exposed && this.tick - p.exposedAt > 30) this.emit({ type: 'radiation' });
    p.exposed = true;
    p.exposedAt = this.tick;
    p.dose += p.starlight > 0 ? 1 : 2;
    if (p.dose >= T.radiationTicks) {
      p.dose -= T.radiationTicks;
      this.emit({ type: 'radiationBurn' });
      this.damagePlayer(0);
    }
  }

  /** True when the player's centre is in radiation above the suit's shielding. */
  private irradiated(p: Player): boolean {
    if (this.room.radiation <= this.suit.shielding) return false;
    const tx = Math.floor((p.x + p.w / 2) / TILE);
    const ty = Math.floor((p.y + p.h / 2) / TILE);
    return hotAt(this.room, p.lane, tx, ty);
  }

  /** True when the player's box overlaps an updraft in its lane. */
  private inVent(p: Player): boolean {
    return this.room.vents.some(
      (v) =>
        v.lane === p.lane &&
        p.x + p.w > v.tx * TILE &&
        p.x < (v.tx + 1) * TILE &&
        p.y + p.h > v.top * TILE &&
        p.y < (v.bottom + 1) * TILE,
    );
  }

  private respawn(): void {
    const p = this.player;
    p.x = p.safeX;
    p.y = p.safeY;
    p.vx = 0;
    p.vy = 0;
    p.laneFrom = p.lane;
    p.lane = p.safeLane;
    p.laneChangedAt = this.tick;
  }

  /** Both bottom corners rest on solid tiles (one-way planks don't count: easy to slip off). */
  private onSolidGround(p: Player): boolean {
    const row = Math.floor((p.y + p.h + 1) / TILE);
    const left = Math.floor(p.x / TILE);
    const right = Math.floor((p.x + p.w - EPS) / TILE);
    return (
      tileAt(this.room, p.lane, left, row) === T_SOLID &&
      tileAt(this.room, p.lane, right, row) === T_SOLID
    );
  }

  private checkExit(): void {
    if (this.exited || this.bossAlive()) return;
    const p = this.player;
    for (const x of this.room.exits) {
      if (x.lane !== p.lane) continue;
      const ex = x.tx * TILE;
      const ey = x.ty * TILE;
      if (p.x < ex + TILE && p.x + p.w > ex && p.y < ey + TILE && p.y + p.h > ey) {
        this.exited = true;
        this.emit({ type: 'roomExit' });
        return;
      }
    }
  }
}

function approach(value: number, target: number, step: number): number {
  if (value < target) return Math.min(value + step, target);
  return Math.max(value - step, target);
}
